// Package imports
import React, { useCallback, useEffect, useRef, useState } from 'react';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';
import { useNavigate, useParams } from 'react-router-dom';

// CSS imports
import './UpdateEmailTemplate.css';

// Component imports
import TextInput from '../../components/inputs/textInput/TextInput';
import PrimaryButton from '../../components/buttons/primaryButton/PrimaryButton';
import FullPageSpinner from '../../components/fullPageSpinner/FullPageSpinner';
import Alert from '../../components/alert/Alert';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';

// Type imports
import { AlertSize } from '../../types/alert/AlertSize';
import { EmailTemplate } from '../../types/emailTemplates/EmailTemplate';

const UpdateEmailTemplate: React.FC = () => {
  const [templateId, setTemplateId] = useState('');
  const [templateDesign, setTemplateDesign] = useState<any>();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [showUnknownError, setShowUnknownError] = useState(false);

  const emailEditorRef = useRef<EditorRef>(null);
  const navigate = useNavigate();
  let { id } = useParams();

  const getTemplate = useCallback(async () => {
    if (id) {
      const res: EmailTemplate | null = await ApiService.getEmailTemplate(id);

      if (res) {
        setName(res.name);
        setTemplateId(res.id);
        setTemplateDesign(res.design);
      }

      setIsPageLoading(false);
    } else {
      setIsPageLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (templateId === '') {
      getTemplate();
    } else {
      setIsPageLoading(false);
    }
  }, [templateId, getTemplate]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'name': {
        setNameError('');
        setName(e.target.value);
        break;
      }
    }
  };

  const triggerSave = () => {
    const isValid = checkInputs();

    if (isValid) {
      save();
    }
  };

  const save = async () => {
    const unlayer = emailEditorRef.current?.editor;

    unlayer?.exportHtml(async (data) => {
      const { design, html } = data;
      setIsLoading(true);

      const res: boolean = await ApiService.updateEmailTemplate(
        templateId,
        name,
        html,
        design
      );

      if (res) {
        navigate('/email-templates');
      } else {
        setShowUnknownError(true);
      }
    });
  };

  const checkInputs = () => {
    let isValid = true;

    if (name === '') {
      setNameError('Please enter a name');
      isValid = false;
    }

    return isValid;
  };

  const onReady: EmailEditorProps['onReady'] = (unlayer) => {
    unlayer.loadDesign(templateDesign);
  };

  return (
    <div className='UpdateEmailTemplate'>
      <SectionWrapper>
        <div className='content'>
          <h1>Update Template</h1>
          {!isPageLoading && templateId !== '' ? (
            <>
              <div className='save-button-container'>
                <PrimaryButton label='Save' onClick={triggerSave} />
              </div>
              <TextInput
                value={name}
                name='name'
                label='Name'
                onChange={onChange}
                errorMessage={nameError}
                className='name-input'
              />
              <EmailEditor ref={emailEditorRef} onReady={onReady} />
            </>
          ) : null}
          {!isPageLoading && templateId === '' ? (
            <div>Email template not found</div>
          ) : null}
        </div>
      </SectionWrapper>
      {isLoading ? <FullPageSpinner /> : null}
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
    </div>
  );
};

export default UpdateEmailTemplate;
